import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GDPRSettings = ({ onClose }) => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const exportUserData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/user/data-export`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Create downloadable file
        const blob = new Blob([JSON.stringify(data, null, 2)], {
          type: 'application/json'
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `koloth-care-data-export-${user.name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success('Your data has been exported and downloaded!');
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      toast.error('Failed to export data. Please try again.');
      console.error('Export error:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteAccount = async () => {
    if (deleteConfirm !== 'DELETE MY ACCOUNT') {
      toast.error('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/user/delete-account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Your deletion request has been logged for admin review', { duration: 8000 });
        setDeleteConfirm('');
        onClose();
      } else {
        throw new Error('Deletion request failed');
      }
    } catch (error) {
      toast.error('Failed to submit deletion request. Please contact the administrator.');
      console.error('Delete error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <Card>
          <CardHeader className="border-b">
            <div className="flex justify-between items-center">
              <CardTitle className="text-2xl">Privacy & Data Settings</CardTitle>
              <Button variant="ghost" onClick={onClose} data-testid="close-gdpr-settings">
                ×
              </Button>
            </div>
            <p className="text-sm text-slate-600">Manage your personal data and privacy preferences</p>
          </CardHeader>
          
          <CardContent className="space-y-6 p-6">
            {/* User Info */}
            <section>
              <h3 className="text-lg font-semibold mb-3">Your Account Information</h3>
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="font-medium text-slate-700">Name:</span>
                    <p className="text-slate-900">{user.name}</p>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">Email:</span>
                    <p className="text-slate-900">{user.email}</p>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">Role:</span>
                    <Badge variant="secondary" className="ml-1">{user.role}</Badge>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">Member Since:</span>
                    <p className="text-slate-900">{new Date(user.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            </section>

            <Separator />

            {/* Data Export */}
            <section>
              <h3 className="text-lg font-semibold mb-3">📥 Export Your Data</h3>
              <p className="text-sm text-slate-600 mb-4">
                Download a complete copy of all your personal data stored in our system. 
                This includes your account information, shift bookings, and timesheet records.
              </p>
              <Button 
                onClick={exportUserData} 
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="export-user-data"
              >
                {loading ? 'Exporting...' : 'Download My Data'}
              </Button>
            </section>

            <Separator />

            {/* Cookie Settings */}
            <section>
              <h3 className="text-lg font-semibold mb-3">🍪 Cookie Preferences</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <div>
                    <p className="font-medium text-green-800">Essential Cookies</p>
                    <p className="text-sm text-green-600">Required for basic functionality</p>
                  </div>
                  <Badge variant="default" className="bg-green-600">Always Active</Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-700">Analytics Cookies</p>
                    <p className="text-sm text-slate-600">We don't use any analytics cookies</p>
                  </div>
                  <Badge variant="secondary">Disabled</Badge>
                </div>
              </div>
            </section>

            <Separator />

            {/* Data Retention */}
            <section>
              <h3 className="text-lg font-semibold mb-3">📅 Data Retention</h3>
              <div className="text-sm text-slate-700 space-y-2">
                <p>• <strong>Active account data:</strong> Stored while your account is active</p>
                <p>• <strong>Payroll & timesheet records:</strong> Retained for 6 years (HMRC legal requirement)</p>
                <p>• <strong>Login sessions:</strong> Expire after 30 minutes of inactivity</p>
                <p>• <strong>Erasure requests:</strong> Reviewed by the administrator within one calendar month</p>
              </div>
            </section>

            <Separator />

            {/* Account Deletion */}
            <section>
              <h3 className="text-lg font-semibold mb-3 text-red-600">🗑️ Request Account Deletion</h3>
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <p className="text-sm text-red-700 mb-4">
                  This submits a <strong>deletion (erasure) request</strong> to the administrator, who will review it
                  within one calendar month. Records needed for payroll, tax or legal purposes may be retained for
                  their statutory period; everything else will be erased or anonymised.
                </p>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-red-700 mb-1">
                      Type "DELETE MY ACCOUNT" to confirm:
                    </label>
                    <input
                      type="text"
                      value={deleteConfirm}
                      onChange={(e) => setDeleteConfirm(e.target.value)}
                      className="w-full px-3 py-2 border border-red-300 rounded-md text-sm"
                      placeholder="DELETE MY ACCOUNT"
                      data-testid="delete-confirmation-input"
                    />
                  </div>
                  
                  <Button 
                    onClick={deleteAccount}
                    disabled={loading || deleteConfirm !== 'DELETE MY ACCOUNT'}
                    variant="destructive"
                    className="w-full"
                    data-testid="delete-account-button"
                  >
                    {loading ? 'Submitting Request...' : 'Submit Deletion Request'}
                  </Button>
                </div>
              </div>
            </section>

            {/* Contact Info */}
            <section className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Need Help?</h4>
              <p className="text-sm text-blue-700">
                For privacy questions or to exercise your GDPR rights, contact our 
                Data Protection Officer at: <strong>privacy@kolothcare.com</strong>
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GDPRSettings;